/**
 * @title The add dialog - Search masterdata for product
 * @description The search for product dialog
 * @image https://storageaccountourimbea2.blob.core.windows.net/tradein2guide/mainpage-additem.png
 */

import {Box, Collapse, DialogContent, Paper, Skeleton, Stack, useMediaQuery} from "@mui/material";
import {StyledButton, StyledTextInput} from "../../../../providers/layout/LayoutProvider";
import React, {forwardRef, useCallback, useMemo, useRef, useState} from "react";
import {useRecoilValue} from "recoil";
import {AccessContextData} from "../../../../providers/access/AppContextProvider";
import {SearchProduct, searchProduct, SearchProductSize} from './ProductSearchApi';
import {AppDataAtom} from "../../../../providers/access/ApplicationDataProvider";
import {makeStyles} from "@mui/styles";
import {LocaleAtom, LocalizationsAtom} from "../../../../providers/localization/LocalizationProvider";
import {Thumbnail} from "../../../../components/Thumbnail";
import {SearchIcon} from "../../../../svg/SvgIcons";
import {debounce} from "lodash";
import {InputMask, InputMaskProps} from "@react-input/mask";
import {LocalizationText} from "../../../../providers/localization/LocalizationText"; // Import the updated LocalizationText component

type ProductSearchProps = {
   onSelect: (product: SearchProduct, size: SearchProductSize) => void
   onSwitch: () => void
}

export const ProductSearch = ({onSelect, onSwitch}: ProductSearchProps) => {
   const localizations = useRecoilValue(LocalizationsAtom);
   const appData = useRecoilValue(AppDataAtom);
   const appContext = useRecoilValue(AccessContextData);
   const classes = useSearchStyles()
   const locale = useRecoilValue(LocaleAtom);
   const isDesktop = useMediaQuery('(min-width:768px)');
   const inputRef = useRef<HTMLInputElement>(null);

   type SearchState = {
      search: string
      result: SearchProduct[] | undefined
   }

   const [searchState, setSearchState] = useState<SearchState>({
      search: '',
      result: undefined
   })

   const onSelecting = async (product: SearchProduct, size: SearchProductSize) => {
      onSelect(product, null!);
   }

   const blurInput = () => {
      inputRef.current?.blur();
   };

   // eslint-disable-next-line react-hooks/exhaustive-deps
   const onChangeDeb = useCallback(debounce((e: any) => {
      const searchTerm = e.target.value;
      if (searchTerm.length < 3) return;

      searchProduct(appData.brandHouseId, appContext.shopId, searchTerm, locale, appContext.currency).then((data) => {
         if (!searchTerm) {
            setSearchState({
               search: searchTerm,
               result: undefined
            })
         } else {
            setSearchState({
               search: searchTerm,
               result: data
            })
         }
         if ((data || []).length > 0) {
            blurInput();
         }
      })
   }, 1200), [])

   const onChange = (e: any) => {
      setSearchState({
         search: e.target.value,
         result: undefined
      })
      if (e.target.value) {
         onChangeDeb(e)
      }
   }

   const ForwardInputMask = forwardRef<HTMLInputElement, InputMaskProps>((props, forwardedRef) => {
      return <InputMask ref={forwardedRef} mask={appData.productSearchMask}
                        replacement={{
                           "_": /\d/,
                           "#": /./
                        }} {...props} />;
   })

   const ProductSearchInput = useMemo(() => {
      return <StyledTextInput
         name={"search"}
         label={localizations.wholeSaleSearchPlaceholder}
         autoComplete={"off"}
         inputRef={inputRef}
         defaultValue={searchState.search}
         InputProps={{
            inputComponent: appData.productSearchMask ? ForwardInputMask : undefined,
            endAdornment: <SearchIcon/>
         }}
         onChange={onChange}
         autoFocus={isDesktop}
      />
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [inputRef]);

   const categoriesActive = appData.brandHouseId !== '64a7f7f54c911bb4866a106b'
      && appData.brandHouseId !== '62711c606207f585302465ba'
      && appData.brandHouseId !== '6756ce01e9eb64be1d79dd26'

   return <DialogContent sx={{backgroundColor: '#f8f8f8'}}>
      <Box sx={{
         display: 'flex',
         flexDirection: 'column',
         justifyContent: 'center',
         alignItems: 'stretch',
         marginTop: '20px',
         marginBottom: '20px'
      }}>
         {ProductSearchInput}
      </Box>
      <Box sx={{display: 'flex', flex: '1 1 auto'}}>
         <Box sx={{
            display: 'flex',
            flexGrow: '1',
            flexDirection: 'column',
            overflow: 'auto',
            gap: '10px'
         }}>
            {searchState.result?.length === 0 ?
               <LocalizationText
                  text={localizations.wholeSaleSearchNotFound}
                  typographyProps={{variant: "h2"}}
                  className={classes.noMatch}
               /> : null}
            {searchState.result?.map((product: SearchProduct) => {
               let wholeSaleTradeInValue;

               if (!product.tradeInValue) {
                  wholeSaleTradeInValue = localizations.wholeSaleExcluded;
               } else if (localizations.wholeSaleValuePrefix.includes('{')) {
                  wholeSaleTradeInValue = localizations.wholeSaleValuePrefix;
               } else {
                  wholeSaleTradeInValue = `${localizations.wholeSaleValuePrefix} ${product.tradeInValue} ${appContext.currency}`.trim();
               }

               return <Paper key={product.id} variant={"outlined"} sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  padding: '10px'
               }}>
                  <Box sx={{width: '180px'}}>
                     <Thumbnail size={120} image={product.image} hideBorder={true}/>
                  </Box>
                  <Box sx={{
                     display: 'flex',
                     flexDirection: 'row',
                     flex: '3 2 0',
                     '@media only screen and (max-width: 700px)': {
                        flexDirection: 'column'
                     }
                  }}>
                     <Box sx={{display: 'flex', justifyContent: 'center', flexDirection: 'column', flex: '3 2 0'}}>
                        <LocalizationText
                           text={product.name}
                           typographyProps={{variant: "h2"}}
                        />
                        <LocalizationText
                           text={wholeSaleTradeInValue}
                           context={{
                              product,
                              currency: appContext.currency
                           }}
                           typographyProps={{
                              variant: "subtitle1",
                              sx: {color: product.tradeInValue === 0 ? '#c0c0c0' : undefined}
                           }}
                        />
                        {(product.tradeInValue !== 0 && localizations.wholeSaleSearchItemSuffix) && <LocalizationText
                           text={localizations.wholeSaleSearchItemSuffix}
                           context={{
                              product,
                              currency: appContext.currency
                           }}
                           typographyProps={{
                              variant: "subtitle1",
                              sx: {color: product.tradeInValue === 0 ? '#c0c0c0' : undefined}
                           }}
                        />}
                     </Box>
                     <Box sx={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '1 1 0',
                        '@media only screen and (max-width: 700px)': {
                           justifyContent: 'start',
                           alignItems: 'start'
                        }
                     }}>
                        <StyledButton disabled={product.tradeInValue === 0} styletype={"main"} onClick={() => {
                           onSelecting(product, null!)
                        }}>
                           {localizations.wholeSaleAddItem2}
                        </StyledButton>
                     </Box>
                  </Box>
               </Paper>
            })}
            {(appData.wholeSaleActive && searchState.result !== undefined && categoriesActive) &&
               <Box sx={{
                  display: 'flex',
                  margin: '20px 0 100px 0',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flex: '1 1 0'
               }}>

                  <StyledButton styletype={"main"} onClick={() => {
                     onSwitch()
                  }}>
                     {localizations.wholeSaleSearchGoCategory}
                  </StyledButton>
               </Box>
            }
         </Box>
      </Box>
      <Collapse orientation={"vertical"} in={!searchState.search}>
         <Stack direction={"row"} className={"c2s-carelabelimage-container"}>
            <img style={{
               width: '50%',
               height: '300px',
               objectFit: 'cover'
            }}
                 src={localizations.wholeSaleSearchImageUrl || "https://storageaccountourimbea2.blob.core.windows.net/attachments/carelabel.jpg"}
                 alt={"Carelabel"}/>
            <Box sx={{marginLeft: '30px', width: '50%'}}>
               <Box sx={{display: 'flex', flexDirection: "column", gap: '20px'}}>
                  <LocalizationText
                     text={localizations.wholeSaleSearchHowToTitle}
                     typographyProps={{variant: "h2", type: "main"}}
                  />
                  <LocalizationText
                     text={localizations.wholeSaleSearchHowToDescription}
                     typographyProps={{variant: "body1", type: "main"}}
                  />
                  <LocalizationText
                     text={localizations.wholeSaleSearchHowToNotFoundTitle}
                     typographyProps={{variant: "body1", type: "main"}}
                  />
                  {(appData.wholeSaleActive && categoriesActive) && <Box>
                     <StyledButton styletype={"main"} onClick={() => {
                        onSwitch()
                     }}>
                        {localizations.wholeSaleSearchGoCategory}
                     </StyledButton>
                  </Box>}
               </Box>
            </Box>
         </Stack>
      </Collapse>
      {searchState.result === undefined && <Collapse orientation={"vertical"} in={!!searchState.search}>
         <Box sx={{display: 'flex', gap: '10px', flexDirection: 'column'}}>
            <Skeleton variant={"rectangular"} width={"100%"} height={"160px"} animation={"pulse"}/>
            <Skeleton variant={"rectangular"} width={"100%"} height={"160px"} animation={"wave"}/>
            <Skeleton variant={"rectangular"} width={"100%"} height={"160px"}/>
            <Skeleton variant={"rectangular"} width={"100%"} height={"160px"}/>
         </Box>
      </Collapse>}
   </DialogContent>
}


const useSearchStyles = makeStyles({
   media: {
      height: 180,
      backgroundSize: 'contain',
      marginLeft: '-20px',
      marginTop: '-20px',
      marginRight: '-20px',
      marginBottom: '15px'
   },
   container: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '10px',
      justifyContent: 'center',
      marginTop: '20px'
   },
   card: {
      width: 'calc(32% - 10px)'
   },
   '@media only screen and (max-width: 800px)': {
      card: {
         width: '100%'
      },
   },
   content: {
      height: '45px',
      overflow: 'hidden'
   },
   noMatch: {
      width: '100%',
      textAlign: 'center'
   },
   actions: {
      display: 'flex',
      flexDirection: 'column'
   },
   sizeContainer: {
      display: 'flex',
      flexDirection: 'row',
      gap: '5px',
      marginBottom: '15px'
   },
   sizeButton: {
      border: '1px solid #808080',
      borderRadius: '4px',
      padding: '4px',
      backgroundColor: '#ffffff',
      cursor: 'pointer'
   },
   sizeButtonSelected: {
      color: '#ffffff',
      backgroundColor: '#a0a0a0'
   },
});
